import React from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import Card from './Card';
import * as styles from './ReorderableList.module.scss';
import type { BasicReorderableItem } from './ReorderableListRow';
import ReorderableListRow from './ReorderableListRow';

interface ReorderableListProps<T> {
  isRemovable?: (item: T, index: number) => boolean;
  items: T[];
  name: string;
  onMove: (sourceIndex: number, destinationIndex: number) => void;
  onRemove?: (index: number) => void;
  renderItem: (item: T, index: number) => JSX.Element;
}

const ReorderableList = <T extends BasicReorderableItem>({
  isRemovable,
  items,
  name,
  onMove,
  onRemove,
  renderItem,
}: ReorderableListProps<T>) => {
  return (
    <DragDropContext
      onDragEnd={({ source, destination }) => {
        if (destination && destination.index !== source.index) {
          onMove(source.index, destination.index);
        }
      }}
    >
      <Card noPadding>
        <Droppable droppableId={`${name}-row-droppable`}>
          {({ droppableProps, innerRef: droppableRef, placeholder }) => (
            <div
              className={styles.orderContainer}
              ref={droppableRef}
              {...droppableProps}
            >
              {items.map((item, index) => (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(
                    { draggableProps, dragHandleProps, innerRef: draggableRef },
                    snapshot,
                  ) => (
                    <div
                      className={styles.draggableContainer}
                      ref={draggableRef}
                      {...draggableProps}
                      {...dragHandleProps}
                    >
                      <ReorderableListRow
                        item={item}
                        index={index}
                        isDragging={snapshot.isDragging}
                        allowRemove={isRemovable?.(item, index) ?? true}
                        onRemove={onRemove}
                        renderItem={renderItem}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {placeholder}
            </div>
          )}
        </Droppable>
      </Card>
    </DragDropContext>
  );
};

export default ReorderableList;
